import L from 'leaflet';
import { get, isString, isEmpty } from 'lodash';
import { scale as vegaScale, scheme as vegaScheme } from 'vega-lib';

import scale from 'utils/scale-value';
import { formatNumber } from 'utils/number-formats';
import { attemptCast } from 'utils/bson';
import {
  CHOROPLETH_ZERO_STATE_MESSAGE,
  DEFAULT_LATLNG_BOUNDS,
} from 'constants/geospatial';
import GeoSpatialChoroplethRole from 'config/map-types/geospatial-choropleth';

/**
 * Generate a choropleth gradient object where the steps are each value divided by the maximum value
 *
 * @param {Object} stageData  The stage data
 * @param {Array} steps       An array of values to be used as steps in the gradient generation
 * @param {Number} min        The minimum value in the matched topoJSON documents
 * @param {Number} max        The maximum value in the matched topoJSON documents
 * @return {Object}           The gradient object created
 */
function generateChoroplethGradient(stageData, steps, min, max) {
  const {
    environment: { customisations },
  } = stageData;

  const color = get(
    customisations.options,
    'colorContinuous.value.color',
    'yellowgreenblue'
  );
  const reverseColor = get(
    customisations.options,
    'colorContinuous.value.reverse',
    false
  );
  const seq = vegaScale('sequential');
  const colorScheme = seq().interpolator(vegaScheme(color));

  const gradient = steps.reduce((acc, val) => {
    const nVal = scale(val, [min, max], [0, 1]);

    return {
      ...acc,
      [val]: reverseColor ? colorScheme(1.0 - nVal) : colorScheme(nVal),
    };
  }, {});

  // For any undefined values, we set the color as transparent
  gradient[undefined] = 'rgba(0, 0, 0, 0)';

  return gradient;
}

/**
 * Create props for the given feature
 *
 * @param {Object} spec     The choropleth spec
 * @param {String} location The location to use
 * @param {Number} count    The count of the feature
 * @return {Object} - The props
 */
function getFeatureProps(spec, location, count) {
  const { customisations, titles } = spec;

  const props = {
    fields: [],
  };

  if (location) {
    props.fields.push({
      key: spec.titles.location,
      value: location,
    });
  }

  if (count) {
    props.fields.push({
      key: titles.color,
      value: formatNumber(count, customisations.color),
    });
  }

  return props;
}

/**
 * If the chart type is a choropleth type, we try to match document locations with the locations
 * in a topoJSON file
 *
 * @param  {Object} stageData  Object containing documents returned from the agg pipeline
 * @return {Object}            Object containing a topoJSON document object and any extra metadata and spec
 */
function generateChoroplethData(stageData) {
  const {
    environment: { chartType, channels },
    metadata,
    documents,
    spec,
    cache,
  } = stageData;

  if (
    chartType !== GeoSpatialChoroplethRole.name ||
    !(channels.location && channels.color)
  ) {
    return {};
  }

  const choroplethMap = cache.topology[spec.topoJSON];

  if (!choroplethMap) {
    // either we're missing geodata, or it's still being loaded.
    return {
      metadata: {
        ...metadata,
        geoJSON: {
          features: [],
        },
        featureMap: {},
      },
      spec: {
        ...spec,
        bounds: DEFAULT_LATLNG_BOUNDS,
        gradient: {},
        zeroStateMessage: undefined,
      },
    };
  }

  const { geoJSON, index } = choroplethMap;

  let min = Number.MAX_SAFE_INTEGER;
  let max = Number.MIN_SAFE_INTEGER;
  let hasMatches;

  const featureMap = {};

  const steps = [];

  documents.forEach(doc => {
    if (!hasMatches) {
      hasMatches = false;
    }

    if (doc.color && isString(doc.location)) {
      const normalisedLocation = doc.location.toLowerCase();
      const featureIndex = index[normalisedLocation];
      if (featureIndex !== undefined) {
        hasMatches = true;

        const feature = geoJSON.features[featureIndex];

        const { id } = feature;
        const count = attemptCast(doc.color);

        featureMap[id] = {
          count,
          ...getFeatureProps(spec, doc.location, count),
        };

        steps.push(count);

        if (count > max) {
          max = count;
        }

        if (count < min) {
          min = count;
        }
      }
    }
  });

  let zeroStateMessage;

  if (hasMatches === false) {
    zeroStateMessage = CHOROPLETH_ZERO_STATE_MESSAGE;
  }

  const gradient = generateChoroplethGradient(stageData, steps, min, max);

  const mapProps = {};

  if (!isEmpty(spec.viewport) && !spec.autoViewport) {
    mapProps.viewport = spec.viewport;
  } else {
    const geoJSONLayer = L.geoJson(geoJSON);
    mapProps.bounds = geoJSONLayer.getBounds();
    mapProps.center = mapProps.bounds.getCenter();
  }

  return {
    metadata: {
      ...metadata,
      geoJSON,
      featureMap,
    },
    spec: {
      ...stageData.spec,
      ...mapProps,
      gradient,
      zeroStateMessage,
    },
  };
}

export default {
  input: [
    'spec',
    'environment.chartType',
    'environment.channels',
    'environment.customisations.options',
    'documents',
    'metadata',
    'cache',
  ],
  fn: generateChoroplethData,
};

export { generateChoroplethData, generateChoroplethGradient, getFeatureProps };
